import React, { useEffect } from 'react';
import PageTitle from '../components/common/PageTitle';
import { useLanguage } from '../contexts/LanguageContext';

const AboutPage: React.FC = () => {
  const { t, isHindi } = useLanguage();

  useEffect(() => {
    document.title = isHindi ? 'हमारे बारे में | लीगल नेस्ट' : 'About Us | Legal Nest';
  }, [isHindi]);
  
  return (
    <div className="container-custom py-10">
      <PageTitle title={t('about_us')} />
      
      <div className="max-w-4xl mx-auto">
        <div className="prose prose-lg max-w-none mb-10">
          {isHindi ? (
            <>
              <p className="lead text-xl font-medium text-neutral-700">
                लीगल नेस्ट भारत का अग्रणी कानूनी समाचार और जानकारी प्लेटफॉर्म है जो कानूनी पेशेवरों, छात्रों और कानूनी मामलों में रुचि रखने वाले आम जनता को सेवाएं प्रदान करता है।
              </p>
              
              <h2>हमारा मिशन</h2>
              <p>
                लीगल नेस्ट का मिशन भारत के कानूनी क्षेत्र में पारदर्शिता और सुगम्यता को बढ़ावा देना है। हम विशेषज्ञ विश्लेषण, समय पर अपडेट और व्यापक कवरेज के माध्यम से कानूनी जानकारी को सभी के लिए सुलभ बनाने का प्रयास करते हैं।
              </p>
              
              <h2>हमारे मूल्य</h2>
              <ul>
                <li><strong>विश्वसनीयता:</strong> हम हमेशा सटीक और विश्वसनीय कानूनी जानकारी प्रदान करने के लिए प्रतिबद्ध हैं।</li>
                <li><strong>स्वतंत्रता:</strong> हम स्वतंत्र और निष्पक्ष कवरेज प्रदान करते हैं, जो किसी भी राजनीतिक या वाणिज्यिक हितों से प्रभावित नहीं होता।</li>
                <li><strong>समावेशिता:</strong> हम कानूनी सूचनाओं को जटिल भाषा से मुक्त करके और क्षेत्रीय भाषाओं में कंटेंट प्रदान करके सभी तक पहुँचाते हैं।</li>
                <li><strong>नवाचार:</strong> हम हमेशा नई तकनीकों और उपकरणों का उपयोग करके कानूनी जानकारी को प्रभावी ढंग से प्रस्तुत करने का प्रयास करते हैं।</li>
              </ul>
              
              <h2>हमारी टीम</h2>
              <p>
                लीगल नेस्ट की टीम अनुभवी कानूनी पत्रकारों, कानूनी विशेषज्ञों, और तकनीकी पेशेवरों से मिलकर बनी है जो देश भर के कानूनी विकास पर नज़र रखते हैं। हमारी टीम न्यायिक निर्णयों, कानूनी संशोधनों, और कानूनी उद्योग के रुझानों पर विस्तृत रिपोर्टिंग प्रदान करती है।
              </p>
              
              <h2>हमारा कवरेज</h2>
              <p>
                हम निम्नलिखित क्षेत्रों में व्यापक कवरेज प्रदान करते हैं:
              </p>
              <ul>
                <li>सुप्रीम कोर्ट और हाई कोर्ट के फैसले</li>
                <li>कानूनी अपडेट और समाचार</li>
                <li>कानूनी शिक्षा और करियर के अवसर</li>
                <li>लॉ फर्मों की गतिविधियाँ और अपडेट</li>
                <li>अंतर्राष्ट्रीय कानूनी विकास</li>
                <li>बेयर एक्ट्स और कानूनी विश्लेषण</li>
              </ul>
              
              <h2>संपर्क में रहें</h2>
              <p>
                हम अपने पाठकों से प्रतिक्रिया और सुझाव का स्वागत करते हैं। आप हमें <a href="mailto:info@legalnest.live">info@legalnest.live</a> पर ईमेल कर सकते हैं या हमारे <a href="/contact">संपर्क फॉर्म</a> का उपयोग कर सकते हैं।
              </p>
            </>
          ) : (
            <>
              <p className="lead text-xl font-medium text-neutral-700">
                Legal Nest is India's leading legal news and information platform serving legal professionals, students, and the general public interested in legal affairs.
              </p>
              
              <h2>Our Mission</h2>
              <p>
                Legal Nest's mission is to promote transparency and accessibility in India's legal domain. We strive to make legal information accessible to everyone through expert analysis, timely updates, and comprehensive coverage.
              </p>
              
              <h2>Our Values</h2>
              <ul>
                <li><strong>Credibility:</strong> We are committed to providing accurate and reliable legal information.</li>
                <li><strong>Independence:</strong> We maintain independent and impartial coverage, free from any political or commercial interests.</li>
                <li><strong>Inclusivity:</strong> We make legal information accessible by removing complex jargon and providing content in regional languages.</li>
                <li><strong>Innovation:</strong> We continuously seek new technologies and tools to effectively present legal information.</li>
              </ul>
              
              <h2>Our Team</h2>
              <p>
                Legal Nest's team consists of experienced legal journalists, legal experts, and technical professionals who keep track of legal developments across the country. Our team provides detailed reporting on judicial decisions, legislative amendments, and trends in the legal industry.
              </p>
              
              <h2>Our Coverage</h2>
              <p>
                We provide extensive coverage in the following areas:
              </p>
              <ul>
                <li>Supreme Court and High Court judgments</li>
                <li>Legal updates and news</li>
                <li>Legal education and career opportunities</li>
                <li>Law firm activities and updates</li>
                <li>International legal developments</li>
                <li>Bare Acts and legal analysis</li>
              </ul>
              
              <h2>Stay Connected</h2>
              <p>
                We welcome feedback and suggestions from our readers. You can email us at <a href="mailto:info@legalnest.live">info@legalnest.live</a> or use our <a href="/contact">contact form</a>.
              </p>
            </>
          )}
        </div>
        
        <div className="flex flex-wrap justify-center gap-6 mb-10">
          <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-md text-center">
            <h3 className="font-serif font-bold text-lg mb-2">Established</h3>
            <p className="text-3xl font-bold text-primary-600">2020</p>
            <p className="text-neutral-600 mt-2">Founded by legal professionals</p>
          </div>
          
          <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-md text-center">
            <h3 className="font-serif font-bold text-lg mb-2">Team</h3>
            <p className="text-3xl font-bold text-primary-600">25+</p>
            <p className="text-neutral-600 mt-2">Legal journalists & experts</p>
          </div>
          
          <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-md text-center">
            <h3 className="font-serif font-bold text-lg mb-2">Monthly Readers</h3>
            <p className="text-3xl font-bold text-primary-600">450,000+</p>
            <p className="text-neutral-600 mt-2">Unique visitors</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;